import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, Clock, ArrowRight, Sparkles } from 'lucide-react';

const Contact = () => {
  const contactItems = [
    {
      icon: Phone,
      title: 'Phone',
      subtitle: 'Immediate assistance',
      value: '+91 91104 16663',
      href: 'tel:+919110416663',
      testid: 'contact-phone'
    },
    {
      icon: Mail,
      title: 'Email',
      subtitle: 'We reply within 2 hours',
      value: 'support@propitstop.in',
      href: 'mailto:support@propitstop.in',
      testid: 'contact-email'
    },
    {
      icon: MapPin,
      title: 'Location',
      subtitle: 'Serving across Bangalore',
      value: 'Bangalore, Karnataka 560077',
      testid: 'contact-location'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      subtitle: 'We are open',
      value: 'Mon – Sat: 9AM – 7PM · Sun: 10AM – 5PM',
      testid: 'contact-hours'
    }
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen bg-slate-950 text-slate-200">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 pt-24 pb-20">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(6,182,212,0.18),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.12),transparent_50%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-medium">Get In Touch</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-3xl">
            Talk to a real <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">car expert</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Got a quote from another garage? Weird noise from your engine? Just drop us a line — we'll help.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Contact Cards */}
            <div className="lg:col-span-5 space-y-4">
              <h2 className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium mb-2">Reach Us</h2>
              {contactItems.map((item, i) => {
                const Icon = item.icon;
                const CardEl = item.href ? 'a' : 'div';
                return (
                  <CardEl
                    key={i}
                    {...(item.href ? { href: item.href } : {})}
                    data-testid={item.testid}
                    className="group block bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-5 hover:border-cyan-500/40 hover:-translate-y-0.5 transition-[transform,border-color,box-shadow] duration-300 hover:shadow-[0_0_22px_rgba(6,182,212,0.15)]"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-cyan-400" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold">{item.title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{item.subtitle}</p>
                        <p className="mt-2 text-slate-300 group-hover:text-cyan-400 transition-colors break-words">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  </CardEl>
                );
              })}
            </div>

            {/* Map */}
            <div className="lg:col-span-7">
              <h2 className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium mb-4">Our Coverage</h2>
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/40 backdrop-blur-xl h-[520px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d248849.84916296514!2d77.46612636249999!3d12.953945599999992!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1670c9b44e6d%3A0xf8dfc3e8517e4fe0!2sBengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.95) contrast(0.85)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bangalore Map"
                ></iframe>
                <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/5" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 border border-slate-800 px-8 py-14 md:px-16 md:py-20">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(6,182,212,0.2),transparent_55%)]" />
            </div>
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-xl">
                <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
                  Ready to service your car?
                </h2>
                <p className="mt-3 text-slate-400">Book your service now — free pickup, expert review within 2 hours.</p>
              </div>
              <Link to="/service/services" data-testid="contact-book-cta">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl px-6 py-6 text-base shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-[box-shadow,background-color] duration-300">
                  Book Service <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
